import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import { StatisticsOverviewDto } from "../../../../data/models/responses/StatisticsResponses";

interface Props {
  overview: StatisticsOverviewDto | undefined;
  isLoading: boolean;
}

const SERIES = [
  { key: "logos", label: "Logos", color: "#1B76DF" },
  { key: "designs", label: "Designs", color: "#7BBB58" },
  { key: "orders", label: "Orders", color: "#E7B400" },
  { key: "users", label: "Users", color: "#9333EA" },
] as const;

type SeriesKey = (typeof SERIES)[number]["key"];

export function TrendChart({ overview, isLoading }: Props) {
  const [enabled, setEnabled] = useState<Record<SeriesKey, boolean>>({
    logos: true,
    designs: true,
    orders: true,
    users: true,
  });

  // Merge the four series into a single data array indexed by date.
  const data = useMemo(() => {
    if (!overview) return [] as Record<string, number | string>[];
    const map = new Map<string, Record<string, number | string>>();

    const merge = (key: SeriesKey, points: { date: string; count: number }[]) => {
      points.forEach((p) => {
        const dateKey = p.date.slice(0, 10);
        const existing = map.get(dateKey) ?? { date: dateKey };
        existing[key] = p.count;
        map.set(dateKey, existing);
      });
    };

    merge("logos", overview.logos.dailySeries);
    merge("designs", overview.designs.dailySeries);
    merge("orders", overview.orders.dailySeries);
    merge("users", overview.users.dailySeries);

    return Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [overview]);

  function toggle(key: SeriesKey) {
    setEnabled((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  return (
    <JetSportBox className="opacity-0 animate-fadeIn" style={{ animationDelay: "320ms", animationFillMode: "forwards" }}>
      <BoxHeader2 className="!py-5 !px-6 items-center justify-between border-b border-borderGray">
        <div className="flex flex-col">
          <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
            Growth
          </span>
          <span className="text-base font-semibold text-black mt-0.5">
            Cumulative totals over time
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {SERIES.map((s) => {
            const active = enabled[s.key];
            return (
              <button
                key={s.key}
                onClick={() => toggle(s.key)}
                type="button"
                className={[
                  "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-all",
                  active
                    ? "border-borderGray bg-white text-black"
                    : "border-borderGray bg-gray-100 text-gray-600 line-through",
                ].join(" ")}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: active ? s.color : "#cbd5e1" }}
                />
                {s.label}
              </button>
            );
          })}
        </div>
      </BoxHeader2>

      <div className="h-[360px] px-2 pt-4 pb-6">
        {isLoading || !overview ? (
          <div className="h-full w-full bg-gray-100/40 animate-pulse rounded-md" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid stroke="#ECEEF9" strokeDasharray="2 6" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#94a3b8"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(d: string) =>
                  new Date(d).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })
                }
              />
              <YAxis
                stroke="#94a3b8"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #ECEEF9",
                  fontSize: 12,
                  boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
                }}
                labelFormatter={(d) =>
                  new Date(String(d)).toLocaleDateString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                }
              />
              <Legend wrapperStyle={{ display: "none" }} />
              {SERIES.map((s) =>
                enabled[s.key] ? (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                  />
                ) : null,
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </JetSportBox>
  );
}
